import { fill, t } from '../strings';
import { visitLineLabel } from './serviceReportDocument';
import type { VisitPendingLine } from './visitPage';

/**
 * 🔒 Which lines a visit's completion sheet lists (piece 29; decisions 2061, 5571): the visit's own lines and any line an entry
 * on the visit names beside them, a visit naming none reading every line (`ownLineIds`), then those an entry on the visit names,
 * or all of them where none does (`sheetLineIds`). The visit's page draws the sheet from these, and Complete the Visit holds
 * the lines still Pending among them, so one rule answers for both.
 */
export function visitOwnLineIds(facts: { jobLineIds: readonly string[]; coveredLineIds: readonly string[]; hoursLineIds: readonly string[] }): string[] {
  const covered = new Set(facts.coveredLineIds);
  const hours = new Set(facts.hoursLineIds);
  return covered.size === 0 ? [...facts.jobLineIds] : facts.jobLineIds.filter((id) => covered.has(id) || hours.has(id));
}

export function visitSheetLineIds(facts: { jobLineIds: readonly string[]; coveredLineIds: readonly string[]; hoursLineIds: readonly string[] }): string[] {
  const hours = new Set(facts.hoursLineIds);
  return visitOwnLineIds(facts).filter((id) => hours.size === 0 || hours.has(id));
}

/** 🔒 T3851: the lines of the sheet still Pending, in the job's order and numbered by their place on it. */
export function pendingSheetLines(lines: readonly { id: string; description: string; completionStatus: string }[], sheetIds: readonly string[]): VisitPendingLine[] {
  const listed = new Set(sheetIds);
  return lines.flatMap((line, index) => (listed.has(line.id) && line.completionStatus === 'Pending' ? [{ lineId: line.id, lineNumber: index + 1, description: line.description }] : []));
}

/**
 * 🔒 T3851 (piece 29): why a visit completed in full, or the sheet shown for it, waits on lines still Pending, naming each; null
 * where none stands. The job's own Complete says the same of the job, in `workOrder.pending.job*`.
 */
export function pendingLinesProblem(pending: readonly VisitPendingLine[] | null | undefined): string | null {
  if (!pending || pending.length === 0) return null;
  const lines = pending.map((row) => visitLineLabel(row.lineNumber, row.description)).join(', ');
  return pending.length === 1 ? fill(t('workOrder.pending.visitOne'), { lines }) : fill(t('workOrder.pending.visitMany'), { count: String(pending.length), lines });
}

/** 🔒 T3851: the job's own Complete refusal, which says job where the job's Complete is meant. */
export function pendingJobLinesProblem(count: number): string | null {
  if (count <= 0) return null;
  return count === 1 ? t('workOrder.pending.jobOne') : fill(t('workOrder.pending.jobMany'), { count: String(count) });
}
